import { groupLifeService } from '@/services';
import type { CoverageSchemaResponse } from '@/features/quotes/types/group-life/coverage-schema.types';
import { isDailyBenefitCoverage } from '@/features/quotes/utils/group-life/daily-benefit';
import type { CoverageSelection } from '@/features/quotes/utils/group-life/coverage-resolver';
import { useEffect, useMemo, useState } from 'react';

/**
 * Busca o MaxDailyRate de cada diária contratada. O teto só limita e rotula
 * o campo. Não preenche o capital quando a cotação veio sem valor — o máximo
 * da tabela (R$ 500 no Vida PME) não é a diária escolhida.
 */
export function useDailyBenefitMax(params: {
  productCode: string;
  schema: CoverageSchemaResponse | null | undefined;
  selections: Record<string, CoverageSelection | undefined>;
}): Record<string, number> {
  const [maxByKey, setMaxByKey] = useState<Record<string, number>>({});

  const wanted = useMemo(() => {
    const items: { coverageCode: string; benefitCode: string }[] = [];
    for (const coverage of params.schema?.coverages ?? []) {
      if (!isDailyBenefitCoverage(coverage.code)) continue;
      const selection = params.selections[coverage.code];
      if (
        selection?.kind !== 'BENEFIT_SELECTION_WITH_SI' ||
        !selection.enabled ||
        !selection.selectedChildCoverageCode
      ) {
        continue;
      }
      items.push({
        coverageCode: coverage.code,
        benefitCode: selection.selectedChildCoverageCode,
      });
    }
    return items;
  }, [params.schema, params.selections]);

  const wantedKey = wanted
    .map(item => `${item.coverageCode}:${item.benefitCode}`)
    .join('|');

  useEffect(() => {
    if (!params.productCode || wanted.length === 0) return;
    let cancelled = false;

    for (const item of wanted) {
      const key = `${item.coverageCode}:${item.benefitCode}`;
      void groupLifeService
        .getDailyBenefitRate({
          productCode: params.productCode,
          coverageCode: item.coverageCode,
          benefitCode: item.benefitCode,
        })
        .then(response => {
          if (cancelled || !response.success || response.data == null) return;
          const max = response.data.maxDailyRate;
          if (!Number.isFinite(max) || max <= 0) return;
          setMaxByKey(prev => (prev[key] === max ? prev : { ...prev, [key]: max }));
        });
    }

    return () => {
      cancelled = true;
    };
    // wantedKey representa a seleção atual; a lista `wanted` é lida no mesmo render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantedKey, params.productCode]);

  return useMemo(() => {
    const maxByCoverage: Record<string, number> = {};
    for (const item of wanted) {
      const max = maxByKey[`${item.coverageCode}:${item.benefitCode}`];
      if (max != null) maxByCoverage[item.coverageCode] = max;
    }
    return maxByCoverage;
  }, [wanted, maxByKey]);
}
